import type { NdiProps } from './types.js';
import {
  calculateNextBitrate,
  closestEncoderProfile,
  DefaultBitrate,
  encoderProfiles
} from '#~/video/constants.js';
import { Annotation, Column, Group } from '@studio/design-system/form';
import * as Select from '@studio/design-system/select';
import { NdiDefaults } from '@studio/models/ndi';
import { VideoDefaults } from '@studio/models/video';
import { useLatestFn } from '@studio/react-utils/hooks';
import { QualityNDI } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';
import type { ReactElement } from 'react';

/** `<Ndi>` settings. */
export const Ndi = ({ settings, ...props }: NdiProps): ReactElement => {
  const current = defaults({}, settings.value, NdiDefaults, VideoDefaults);

  const heightStr = String(current.query?.height ?? '1080');
  const height = Number.parseInt(heightStr, 10);

  const bitrateStr = current.query?.ndiBitrate;
  const bitrate = bitrateStr ? Number.parseInt(String(bitrateStr), 10) : DefaultBitrate;

  const encoderProfile = closestEncoderProfile(bitrate, height);

  const handleFrameRateChange = useLatestFn((value: string) => {
    const frameRate = value;

    if (frameRate && frameRate !== current.query?.ndiFramerate) {
      const heightStr = String(current.query?.height ?? '1080');
      const height = Number.parseInt(heightStr, 10);

      const ndiBitrate = calculateNextBitrate(encoderProfile, height);

      const query = { ...current.query, ndiFramerate: frameRate, ndiBitrate };

      settings({ ...current, query });
    }
  });

  const handleBitrateChange = useLatestFn((bitrate: number) => {
    const query = { ...current.query, ndiBitrate: bitrate };

    settings({ ...current, query });
  });

  const handleEncoderProfileChange = useLatestFn((profile: string) => {
    const heightStr = String(current.query?.height ?? '1080');
    const height = Number.parseInt(heightStr, 10);

    const bitrate = calculateNextBitrate(profile, height);

    handleBitrateChange(bitrate);
  });

  const handleNDIBandwidthModeChange = useLatestFn((value: string) => {
    const ndiBandwidthMode = Number.parseInt(value, 10) as QualityNDI;

    const query = { ...current.query, ndiBandwidthMode };

    settings({ ...current, query });
  });

  const handleNDIFrameSyncModeChange = useLatestFn((value: string) => {
    const ndiFrameSyncMode = Number.parseInt(value, 10);

    const query = { ...current.query, ndiFrameSyncMode };

    settings({ ...current, query });
  });

  const frameRateOptions = ['60/1', '30/1', '15/1'];

  return (
    <Column {...props}>
      <Group label='NDI Bandwidth Mode'>
        <Select.Root
          onValueChange={handleNDIBandwidthModeChange}
          value={(
            (current.query?.ndiBandwidthMode as T_Maybe<number>) ?? QualityNDI.HIGH
          ).toString()}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>

          <Select.Content>
            <Select.Item value={QualityNDI.HIGH.toString()}>High</Select.Item>
            <Select.Item value={QualityNDI.LOW.toString()}>Low</Select.Item>
          </Select.Content>
        </Select.Root>
      </Group>

      <Group label='NDI Frame Sync Mode'>
        <Select.Root
          onValueChange={handleNDIFrameSyncModeChange}
          value={((current.query?.ndiFrameSyncMode as T_Maybe<number>) ?? '1').toString()}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>

          <Select.Content>
            <Select.Item value={'1'}>On</Select.Item>
            <Select.Item value={'0'}>Off</Select.Item>
          </Select.Content>
        </Select.Root>
      </Group>

      <Group annotation={<Annotation.Required />} label='Frame Rate'>
        <Select.Root
          onValueChange={handleFrameRateChange}
          value={(current.query?.ndiFramerate as T_Maybe<string>) ?? '30'}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>

          <Select.Content>
            {frameRateOptions.map((a) => (
              <Select.Item key={a} value={a.split('/')[0] ?? ''}>
                {a}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Group>

      <Group label='Encoder Profile'>
        <Select.Root onValueChange={handleEncoderProfileChange} value={encoderProfile}>
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>

          <Select.Content>
            {encoderProfiles.map((a) => (
              <Select.Item key={a} value={a}>
                {a}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        {/*
        <Input
          onChange={(e) => handleBitrateChange(Number.parseInt(e, 10))}
          placeholder='Bitrate'
          type='number'
          value={String(current.query?.bitrate)}
        />*/}
      </Group>
    </Column>
  );
};
